import React, { useState } from "react";

function DislikeModal({ show, onCancel, onSubmit, partNumber, position }) {
    const [correctPartNumber, setCorrectPartNumber] = useState("");
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [additionalNotes, setAdditionalNotes] = useState("");

    const issues = [
        {
            id: "incorrect",
            label: "Incorrect part match",
            description: "The suggested part doesn't match my needs",
        },
        {
            id: "missing",
            label: "Missing information",
            description: "Important details or specifications are missing",
        },
        {
            id: "other",
            label: "Other",
            description: "None of the above (please specify below)",
        },
    ];

    const handleCancel = () => {
        setCorrectPartNumber("");
        setSelectedIssue(null);
        setAdditionalNotes("");
        onCancel();
    };

    const handleSubmit = () => {
        onSubmit({
            correctPartNumber,
            selectedIssue,
            additionalNotes,
        });
        setCorrectPartNumber("");
        setSelectedIssue(null);
        setAdditionalNotes("");
    };

    const handleIssueSelect = (issueId) => {
        setSelectedIssue((prev) => (prev === issueId ? null : issueId));
    };

    if (!show) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/[0.15] z-40"
                onClick={handleCancel}
            />
            {/* Modal */}
            <div
                className="w-96 p-6 fixed z-50 bg-white rounded-xl shadow-[0px_4px_36.599998474121094px_0px_rgba(0,0,0,0.30)] flex flex-col gap-6 animate-fade-in"
                style={{
                    ...(position.bottom !== undefined
                        ? { bottom: `${position.bottom}px` }
                        : { top: `${position.top}px` }),
                    right: `${position.right}px`,
                }}
            >
                <div className="flex flex-col gap-2 text-left">
                    <div className="text-neutral-900 text-base font-normal font-sf-pro leading-tight">
                        Sorry this part wasn't a match
                    </div>
                    <div className="text-neutral-500 text-sm font-normal font-sf-pro leading-tight break-words">
                        Help us understand what went wrong so we can improve
                        your next lookup
                    </div>
                </div>

                <div className="self-stretch flex flex-col justify-start items-start gap-3">
                    <div className="justify-center text-neutral-900 text-sm font-normal font-sf-pro leading-none">
                        Correct part number (if known)
                    </div>
                    <div className="self-stretch pl-[5px] pr-px py-px bg-white rounded outline outline-1 outline-offset-[-1px] outline-neutral-200 flex flex-col justify-start items-start">
                        <input
                            type="text"
                            value={correctPartNumber}
                            onChange={(e) =>
                                setCorrectPartNumber(e.target.value)
                            }
                            placeholder="Correct part number"
                            className="self-stretch p-2 flex flex-col justify-start items-start focus:outline-none text-neutral-900 text-sm font-normal font-sf-pro leading-tight placeholder:text-neutral-400"
                        />
                    </div>
                </div>

                <div className="self-stretch flex flex-col justify-start items-start gap-3">
                    <div className="justify-center text-neutral-900 text-sm font-normal font-sf-pro leading-none">
                        What went wrong? <span className="text-red-600">*</span>
                    </div>
                    <div className="self-stretch flex flex-col justify-start items-start gap-2">
                        {issues.map((issue) => (
                            <button
                                key={issue.id}
                                type="button"
                                onClick={() => handleIssueSelect(issue.id)}
                                className={`self-stretch px-[15px] py-[11px] bg-white rounded border inline-flex justify-start items-center gap-4 hover:bg-gray-50 transition ${
                                    selectedIssue === issue.id
                                        ? "border-zinc-900"
                                        : "border-neutral-200"
                                }`}
                            >
                                <div
                                    className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center ${
                                        selectedIssue === issue.id
                                            ? "border-zinc-900 bg-zinc-900"
                                            : "border-neutral-300"
                                    }`}
                                >
                                    {selectedIssue === issue.id && (
                                        <svg
                                            width="12"
                                            height="12"
                                            viewBox="0 0 12 12"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M10 3L4.5 8.5L2 6"
                                                stroke="white"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    )}
                                </div>
                                <div className="flex-1 flex justify-start items-center">
                                    <div className="inline-flex flex-col justify-start items-start">
                                        <div className="justify-center text-neutral-900 text-sm font-medium font-inter leading-tight">
                                            {issue.label}
                                        </div>
                                        <div className="justify-center text-neutral-500 text-xs font-normal font-inter leading-none">
                                            {issue.description}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="self-stretch flex flex-col justify-start items-start gap-3">
                    <div className="justify-center text-neutral-900 text-sm font-normal font-sf-pro leading-none">
                        Additional notes
                    </div>
                    <div className="self-stretch pl-[5px] pr-px py-px bg-white rounded outline outline-1 outline-offset-[-1px] outline-neutral-200 flex flex-col justify-start items-start">
                        <textarea
                            value={additionalNotes}
                            onChange={(e) => setAdditionalNotes(e.target.value)}
                            placeholder="Optional Feedback"
                            className="self-stretch min-h-24 px-2 pt-1.5 pb-16 flex flex-col justify-start items-start overflow-hidden resize-none focus:outline-none text-neutral-900 text-sm font-normal font-sf-pro leading-tight placeholder:text-neutral-400"
                        />
                    </div>
                </div>

                <div className="self-stretch inline-flex justify-end items-center gap-3">
                    <button
                        onClick={handleCancel}
                        className="h-8 px-3 py-1.5 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-200 flex justify-center items-center hover:bg-gray-50 transition"
                        type="button"
                    >
                        <div className="inline-flex flex-col justify-start items-center">
                            <div className="text-center justify-center text-neutral-700 text-sm font-sf-pro leading-tight">
                                Cancel
                            </div>
                        </div>
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!selectedIssue}
                        className={`h-8 px-3 py-1.5 bg-zinc-900 rounded-lg flex justify-center items-center transition ${
                            !selectedIssue
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:bg-zinc-800"
                        }`}
                        type="button"
                    >
                        <div className="inline-flex flex-col justify-start items-center">
                            <div className="text-center justify-center text-white text-sm font-sf-pro leading-tight">
                                Submit Feedback
                            </div>
                        </div>
                    </button>
                </div>
            </div>
        </>
    );
}

export default DislikeModal;
